import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchEngagement, fetchLearning } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

type Period = "7d" | "30d" | "90d";

export function AnalyticsPage() {
  const [period, setPeriod] = useState<Period>("30d");

  const engagement = useQuery({ queryKey: ["engagement", period], queryFn: () => fetchEngagement(period) });
  const learning = useQuery({ queryKey: ["learning", period], queryFn: () => fetchLearning(period) });

  const e = engagement.data?.data ?? {};
  const l = learning.data?.data ?? {};

  const chartData = [
    { name: "Sessions", value: (e as Record<string, number>).totalSessions ?? 0 },
    { name: "Active Children", value: (e as Record<string, number>).activeChildren ?? 0 },
    { name: "Completions", value: (l as Record<string, number>).totalCompletions ?? 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Analytics</h1>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {(["7d", "30d", "90d"] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                period === p ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {engagement.isLoading || learning.isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Metric label="Total Sessions" value={String((e as Record<string, number>).totalSessions ?? 0)} />
            <Metric label="Active Children" value={String((e as Record<string, number>).activeChildren ?? 0)} />
            <Metric label="3-Star Rate" value={`${(l as Record<string, number>).threeStarRate ?? 0}%`} />
            <Metric label="Avg Accuracy" value={`${(l as Record<string, number>).avgAccuracy ?? 0}%`} />
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold">Activity Overview</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#FF8C42" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
